import {
  Adherent,
  LinkAdherentToUser as LinkAdherentToUser,
  UserAdherents,
} from './../interfaces/adherents/adherents.model';
import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
} from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { catchError, Observable, Subject, takeUntil, throwError } from 'rxjs';
import { ConfigService } from './core/config.service';
import { UserKeycloak } from '../interfaces/keycloak/userKeycloak';

@Injectable({
  providedIn: 'root',
})
export class AdherentService implements OnDestroy {
  private readonly _destroying$ = new Subject<void>();

  constructor(
    private readonly configService: ConfigService,
    private readonly http: HttpClient,
  ) {}

  /**
   * Called when the component is destroyed. Unsubscribes from all subscriptions to prevent memory leaks.
   */
  ngOnDestroy() {
    this._destroying$.next();
    this._destroying$.complete();
  }

  /**
   * Retrieves a list of all Adherent objects from the backend API.
   * @returns An Observable of the list of Adherent objects.
   */
  public fetchAllAdherent(): Observable<Adherent[]> {
    return this.http
      .get<
        Adherent[]
      >(`${this.configService.config.backendUri}/Adherent/ListRefAdherent`)
      .pipe(
        takeUntil(this._destroying$),
        catchError((httpErrorResponse: HttpErrorResponse) => {
          const error = httpErrorResponse;
          return throwError(() => error);
        }),
      );
  }

  /**
   * Retrieves a list of all KeycloakUser that are Adhrent from the backend API.
   * @returns An Observable of the list of UserKeycloak objects.
   */
  public fetchAllUsersKeycloakAdherents(): Observable<UserKeycloak[]> {
    return this.http
      .get<
        UserKeycloak[]
      >(`${this.configService.config.backendUri}/Adherent/ListKeycloakUserAdherents`)
      .pipe(
        takeUntil(this._destroying$),
        catchError((httpErrorResponse: HttpErrorResponse) => {
          const error = httpErrorResponse;
          return throwError(() => error);
        }),
      );
  }

  /**
   * Retrieves a list of all UserKeycloak objects that are Adherents from the backend API.
   * @returns An Observable of the list of UserAD objects.
   */
  public ListKeycloakUserWithoutAdherents(): Observable<UserKeycloak[]> {
    return this.http
      .get<
        UserKeycloak[]
      >(`${this.configService.config.backendUri}/Admin/ListKeycloakUserWithoutAdherents`)
      .pipe(
        takeUntil(this._destroying$),
        catchError((httpErrorResponse: HttpErrorResponse) => {
          const error = httpErrorResponse;
          return throwError(() => error);
        }),
      );
  }

  /**
   * Retrieves a list of UserAdherent objects by isAdherent from the backend API.
   * @param idUser The idAdherent filtering on UserAdherent.
   * @returns An Observable of the list of UserAdherent objects.
   */
  public fetchUserAdherentsByAdherent(
    idAdherent: number,
  ): Observable<UserAdherents[]> {
    return this.http
      .get<
        UserAdherents[]
      >(`${this.configService.config.backendUri}/Admin/ListUserAdherentsByAdherent?idAdherent=${idAdherent}`)
      .pipe(
        takeUntil(this._destroying$),
        catchError((httpErrorResponse: HttpErrorResponse) => {
          const error = httpErrorResponse;
          return throwError(() => error);
        }),
      );
  }

  /**
   * Retrieves a list of UserAdherent objects by isAdherent from the backend API.
   * @param idUser The idUser filtering on UserAdherent.
   * @returns An Observable of the list of UserAdherent objects.
   */
  public fetchUserAdherentByUser(email: string): Observable<UserAdherents[]> {
    return this.http
      .get<
        UserAdherents[]
      >(`${this.configService.config.backendUri}/Admin/ListUserAdherentsByUser?email=${encodeURIComponent(email)}`)
      .pipe(
        takeUntil(this._destroying$),
        catchError((httpErrorResponse: HttpErrorResponse) => {
          const error = httpErrorResponse;
          return throwError(() => error);
        }),
      );
  }

  /**
   * Links a list of LinkAdherentToUser objects to user accounts by making a POST request to the backend API.
   * @param adherentToUsers The list of LinkAdherentToUser objects to link to user accounts.
   * @param activiteAdherent The activity type ('gpo' or 'standard') for Keycloak group assignment.
   * @param isTitulaire Whether the user should have titulaire role.
   * @returns An Observable of the updated list of LinkAdherentToUser objects.
   */
  public linkAdherentToUser(
    adherentToUsers: LinkAdherentToUser[],
    activiteAdherent: string = 'standard',
    isTitulaire: boolean = false,
  ): Observable<LinkAdherentToUser[]> {
    const params = new URLSearchParams();
    params.set('activiteAdherent', activiteAdherent);
    params.set('isTitulaire', isTitulaire.toString());

    return this.http
      .post<
        LinkAdherentToUser[]
      >(`${this.configService.config.backendUri}/Admin/LinkAdherentToUser?${params.toString()}`, adherentToUsers)
      .pipe(
        takeUntil(this._destroying$),
        catchError((httpErrorResponse: HttpErrorResponse) => {
          const error = httpErrorResponse;
          return throwError(() => error);
        }),
      );
  }

  /**
   * Deletes a list of LinkAdherentToUser objects by making a DELETE request to the backend API.
   * @param adherentToUsers The list of LinkAdherentToUser objects to delete.
   * @returns An Observable of the deleted list of LinkAdherentToUser objects.
   */
  public deleteAdherentsToUser(
    adherentToUsers: LinkAdherentToUser[],
  ): Observable<LinkAdherentToUser[]> {
    const options = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
      body: adherentToUsers,
    };

    return this.http
      .delete<
        LinkAdherentToUser[]
      >(`${this.configService.config.backendUri}/Admin/DeleteUserAdherents`, options)
      .pipe(
        takeUntil(this._destroying$),
        catchError((httpErrorResponse: HttpErrorResponse) => {
          const error = httpErrorResponse;
          return throwError(() => error);
        }),
      );
  }
}
